// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/formatDetect.js
//
// Format detection reads content, not extensions. The extension is only a
// tie-breaker between formats whose bytes look alike (for example a ZIP that
// could be a Procreate brush or a generic collection).
import { isZipBytes } from './safeZip.js';
import { isPngBytes, readPngTextChunks } from './kritaPreset.js';

export const BRUSH_FORMATS = Object.freeze({
  ZIP: 'zip',
  KPP: 'krita-kpp',
  GBR: 'gimp-gbr',
  GIH: 'gimp-gih',
  ABR: 'photoshop-abr',
  MYB: 'mypaint-myb',
  PNG: 'image-png',
  JPEG: 'image-jpeg',
  WEBP: 'image-webp',
  SVG: 'image-svg',
  UNKNOWN: 'unknown',
});

export const ACCEPTED_BRUSH_EXTENSIONS = Object.freeze([
  '.brushset', '.brush', '.brushlibrary', '.zip', '.bundle',
  '.kpp', '.gbr', '.gih', '.myb', '.abr',
  '.png', '.jpg', '.jpeg', '.webp', '.svg',
]);

export function fileExtension(name) {
  const clean = String(name || '').toLowerCase().split('?')[0].split('#')[0];
  const slash = clean.lastIndexOf('/');
  const base = slash >= 0 ? clean.slice(slash + 1) : clean;
  const dot = base.lastIndexOf('.');
  return dot <= 0 ? '' : base.slice(dot);
}

function ascii(bytes, start, length) {
  let out = '';
  for (let i = start; i < start + length && i < bytes.length; i += 1) out += String.fromCharCode(bytes[i]);
  return out;
}

function readU16Be(bytes, offset) {
  return (bytes[offset] << 8) | bytes[offset + 1];
}

function readU32Be(bytes, offset) {
  return ((bytes[offset] << 24) >>> 0) + (bytes[offset + 1] << 16) + (bytes[offset + 2] << 8) + bytes[offset + 3];
}

export function looksLikeGbr(bytes, offset = 0) {
  if (!bytes || bytes.length < offset + 28) return false;
  const headerSize = readU32Be(bytes, offset);
  const version = readU32Be(bytes, offset + 4);
  return ascii(bytes, offset + 20, 4) === 'GIMP' && headerSize >= 28 && headerSize < 4096 && (version === 1 || version === 2 || version === 3);
}

export function looksLikeGih(bytes) {
  if (!bytes || bytes.length < 64) return false;
  const firstNewline = bytes.indexOf(10);
  if (firstNewline <= 0 || firstNewline > 512) return false;
  const secondNewline = bytes.indexOf(10, firstNewline + 1);
  if (secondNewline <= firstNewline || secondNewline > 4096) return false;
  const params = ascii(bytes, firstNewline + 1, secondNewline - firstNewline - 1);
  return /^\s*\d+\s/.test(params) && /ncells:\s*\d+/.test(params) && looksLikeGbr(bytes, secondNewline + 1);
}

export function looksLikeAbr(bytes) {
  if (!bytes || bytes.length < 8) return false;
  const version = readU16Be(bytes, 0);
  if (version === 1 || version === 2) {
    const count = readU16Be(bytes, 2);
    if (count < 1 || count > 10000) return false;
    const type = readU16Be(bytes, 4);
    return type === 1 || type === 2;
  }
  if (version === 6 || version === 7 || version === 10) {
    const subVersion = readU16Be(bytes, 2);
    return (subVersion === 1 || subVersion === 2) && ascii(bytes, 4, 4) === '8BIM';
  }
  return false;
}

export function looksLikeMyPaint(bytes) {
  if (!bytes || !bytes.length) return false;
  const head = ascii(bytes, 0, Math.min(bytes.length, 4096)).replace(/^﻿/, '').trimStart();
  if (head.startsWith('{')) return /"settings"\s*:/.test(head) && /"base_value"\s*:|"parent_brush_name"\s*:|"version"\s*:/.test(head);
  return /^(#.*\n)*\s*version\s+[23]\b/m.test(head) && /\bradius_logarithmic\b|\bopaque\b/.test(head);
}

export function isKppPng(bytes) {
  if (!isPngBytes(bytes)) return false;
  return readPngTextChunks(bytes).some((chunk) => /preset/i.test(chunk.keyword) || chunk.text.includes('<Preset'));
}

export function detectBrushFormat(input, fileName = '') {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input || new ArrayBuffer(0));
  const extension = fileExtension(fileName);
  if (isZipBytes(bytes)) return { format: BRUSH_FORMATS.ZIP, extension, container: true };
  if (isPngBytes(bytes)) {
    if (isKppPng(bytes)) return { format: BRUSH_FORMATS.KPP, extension, container: false };
    return { format: BRUSH_FORMATS.PNG, extension, container: false };
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return { format: BRUSH_FORMATS.JPEG, extension, container: false };
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') return { format: BRUSH_FORMATS.WEBP, extension, container: false };
  if (looksLikeGbr(bytes)) return { format: BRUSH_FORMATS.GBR, extension, container: false };
  if (looksLikeGih(bytes)) return { format: BRUSH_FORMATS.GIH, extension, container: false };
  if (looksLikeAbr(bytes)) return { format: BRUSH_FORMATS.ABR, extension, container: false };
  if (looksLikeMyPaint(bytes)) return { format: BRUSH_FORMATS.MYB, extension, container: false };
  const head = ascii(bytes, 0, Math.min(bytes.length, 1024)).trimStart();
  if (/^(<\?xml[^>]*>\s*)?(<!--[\s\S]*?-->\s*)*<svg[\s>]/i.test(head)) return { format: BRUSH_FORMATS.SVG, extension, container: false };
  if (head.includes('<Preset') && extension === '.kpp') return { format: BRUSH_FORMATS.KPP, extension, container: false };
  return { format: BRUSH_FORMATS.UNKNOWN, extension, container: false };
}

// Classifies an opened ZIP by its structure.
export function classifyZip(zip, fileName = '') {
  const paths = zip.entries.map((entry) => entry.path);
  const lower = paths.map((path) => path.toLowerCase());
  const extension = fileExtension(fileName);
  const mimetype = zip.find('mimetype');
  let mime = '';
  if (mimetype && mimetype.size < 256 && !mimetype.rejected) {
    try {
      mime = zip.readText(mimetype).trim();
    } catch {}
  }
  if (mime === 'application/x-krita-resourcebundle' || (lower.includes('meta-inf/manifest.xml') && lower.some((p) => p.startsWith('paintoppresets/')))) {
    return 'krita-bundle';
  }
  if (lower.some((p) => p === 'brush.archive')) return 'procreate-brush';
  if (lower.some((p) => p.endsWith('brushset.plist')) || lower.some((p) => /(^|\/)[^/]+\/brush\.archive$/.test(p))) {
    return extension === '.brushlibrary' ? 'procreate-library' : 'procreate-brushset';
  }
  return 'collection';
}
